import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Communicator } from '../../tools/Communication';
import { MerchantContainerInformationNumberOnly } from '../../views/MerchantComponents/MerchantModel';
import i18n from '@/utils/i18n';
import { useSingleInvoiceCollectionQuery } from '../../graphql/queries/singleInvoiceCollection.generated';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { LayoutStoreInstance } from '../../LayoutStore';
import { useUpdateInvoiceCollectionMutation } from '../../graphql/mutations/updateInvoiceCollection.generated';
import { InvoiceCollectionDetailFragment } from '../../graphql/fragments/invoiceCollectionDetail.generated';
import { useUpdateInvoiceCollectionBillomatClientMutation } from '../../graphql/mutations/updateInvoiceCollectionBillomatClient.generated';
import {
  DunningReminderBlockType,
  InvoiceCollectionScope,
} from '../../graphql/generatedTypes';
import {
  TabsComponentBootstrap,
  LoadingContainer,
} from '../../components/Layout';
import { InvoiceCollectionSettingsTab } from './components/InvoiceCollectionSettingsTab';
import { InvoiceCollectionInvoicesTab } from './components/InvoiceCollectionInvoicesTab';
import { InvoiceCollectionStoresAccountsTab } from './components/InvoiceCollectionStoresAccountsTab';
import { InvoiceCollectionChangeHistory } from './components/InvoiceCollectionChangeHistory';
import { FaCog, FaFileInvoice, FaLink, FaHistory } from 'react-icons/fa';
import { Badge, Button } from 'reactstrap';

export function InvoiceCollection() {
  // @ts-ignore
  const { id } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const activeTabParam = urlParams.get('activeTab');
  const activeTab = activeTabParam !== null ? activeTabParam : '0';

  const {
    data,
    loading,
    error,
    refetch: refetchInvoice,
  } = useSingleInvoiceCollectionQuery({
    variables: { id },
  });
  const [updateInvoiceCollection, { loading: updatingInvoiceCollection }] =
    useUpdateInvoiceCollectionMutation();

  const [formData, setFormData] = useState<any>({});
  const [invoiceCollection, setInvoiceCollection] =
    useState<InvoiceCollectionDetailFragment>({
      name: 'invalid',
      id: '',
      invoice_collection_debitor_account_number: 1,
      scope: InvoiceCollectionScope.All,
      stores: [],
      excluded_stores: [],
      partner_accounts: [],
    });
  const [disableAutoSend, setDisableAutoSend] = useState(false);
  const [collapseInvoiceItems, setCollapseInvoiceItems] = useState(false);
  const [needsInvoiceCollectionBreakdown, setNeedsInvoiceCollectionBreakdown] =
    useState(false);
  const [containerPrices, setContainerPrices] = useState<
    MerchantContainerInformationNumberOnly[]
  >([]);

  const [updateBillomatClient, { loading: updatingBillomatClient }] =
    useUpdateInvoiceCollectionBillomatClientMutation({
      variables: {
        id,
      },
      onCompleted: () => {
        LayoutStoreInstance.setSuccess('Billomat client updated successfully');
      },
      onError: (error) => {
        LayoutStoreInstance.setError(
          'Error updating billomat client: ' + error.message,
        );
      },
    });

  // the checkbox are a controlled component, so we need to track the state extra and init the default once
  useEffect(() => {
    const invoiceCollection = data?.invoiceCollection;
    if (invoiceCollection != null) {
      setInvoiceCollection(invoiceCollection);
      setDisableAutoSend(
        invoiceCollection.disable_automatic_partner_invoice_sending || false,
      );
      setCollapseInvoiceItems(
        invoiceCollection.collapse_invoice_items || false,
      );
      setNeedsInvoiceCollectionBreakdown(
        invoiceCollection.needs_invoice_collection_breakdown || false,
      );
      // Initialize form data with new fields
      setFormData((prev: any) => ({
        ...prev,
        regular_payment_target_days:
          invoiceCollection.regular_payment_target_days || 14,
        sepa_2_percent_discount:
          invoiceCollection.sepa_2_percent_discount || false,
        dunning_reminder_block_type:
          invoiceCollection.dunning_reminder_block_type || null,
        dunning_reminder_block_reason:
          invoiceCollection.dunning_reminder_block_reason || '',
        inkassokennzeichen: invoiceCollection.inkassokennzeichen || false,
        needs_invoice_collection_breakdown:
          invoiceCollection.needs_invoice_collection_breakdown || false,
      }));
      Communicator.PostData(
        'container/containerPricesForStores',
        invoiceCollection.stores
          .map((store) => store?.external_vytal_id)
          .filter((id) => id != null),
      )
        .then((res) => res.json())
        .then((prices) => setContainerPrices(prices));
    }
  }, [data?.invoiceCollection]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handlePaymentTargetDaysChange = (e: any) => {
    const value = e.target.value;
    // Strip all non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    setFormData((prev: any) => ({
      ...prev,
      regular_payment_target_days: numericValue,
    }));
  };

  const handleAddressChange = (e: any) => {
    const { name, value } = e.target;
    // always update the whole address since the address can be resolved from store
    // if we update one field the address will no longer be resolved from store and
    // we need to make sure we save the rest as well
    const invoiceAddressWithTypeName: any =
      invoiceCollection.invoice_address || {};
    const { __typename, ...invoiceAddress } = invoiceAddressWithTypeName;
    setFormData((prev: any) => ({
      ...prev,
      // @ts-ignore
      invoice_address: {
        ...invoiceAddress,
        ...prev.invoice_address,
        [name]: value,
      },
    }));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    // Validate dunning reminder block reason if type is PERMANENTLY
    if (
      formData.dunning_reminder_block_type ===
        DunningReminderBlockType.Permanently &&
      !formData.dunning_reminder_block_reason?.trim()
    ) {
      LayoutStoreInstance.setError(
        i18n.t('Invoices:dunningReminderBlockReasonRequired'),
      );
      return;
    }
    // Prepare payload - send dunning_reminder_block_reason for both types
    const payload = {
      ...formData,
      regular_payment_target_days: formData.regular_payment_target_days
        ? Number(formData.regular_payment_target_days) || null
        : null,
      dunning_reminder_block_reason:
        formData.dunning_reminder_block_reason?.trim() || null,
      // Cleared text fields must reach the backend as null, not as an empty
      // string: an empty external id would otherwise look configured.
      ...(formData.external_id !== undefined
        ? { external_id: formData.external_id?.trim() || null }
        : {}),
      ...(formData.purchasing_order_email !== undefined
        ? {
            purchasing_order_email:
              formData.purchasing_order_email?.trim() || null,
          }
        : {}),
    };
    console.log(payload);
    updateInvoiceCollection({ variables: { id, payload } })
      .then(() => LayoutStoreInstance.setSuccess('saving successful'))
      .catch((error) => {
        console.error(error);
        LayoutStoreInstance.setError(
          'Error updating invoice collection: ' + error.message,
        );
      });
  };

  if (loading || isNullOrWhitespace(invoiceCollection.id)) {
    return (
      <div className="mt-3">
        <LoadingContainer />
      </div>
    );
  }
  if (error) return <p>Error: {error.message}</p>;

  const billomatLink = `https://pickpack2019.billomat.net/app/invoices?search[client]=${encodeURIComponent(
    invoiceCollection.billomat_client_id || '',
  )}`;

  const tabs = [
    {
      title: i18n.t('InvoiceCollection:settings'),
      icon: <FaCog />,
      content: (
        <InvoiceCollectionSettingsTab
          invoiceCollectionId={id}
          invoiceCollection={invoiceCollection}
          formData={formData}
          setFormData={setFormData}
          handleChange={handleChange}
          handlePaymentTargetDaysChange={handlePaymentTargetDaysChange}
          handleAddressChange={handleAddressChange}
          handleSubmit={handleSubmit}
          updatingInvoiceCollection={updatingInvoiceCollection}
          disableAutoSend={disableAutoSend}
          setDisableAutoSend={setDisableAutoSend}
          collapseInvoiceItems={collapseInvoiceItems}
          setCollapseInvoiceItems={setCollapseInvoiceItems}
          needsInvoiceCollectionBreakdown={needsInvoiceCollectionBreakdown}
          setNeedsInvoiceCollectionBreakdown={
            setNeedsInvoiceCollectionBreakdown
          }
          billomatLink={billomatLink}
        />
      ),
    },
    {
      title: i18n.t('InvoiceCollection:invoices'),
      icon: <FaFileInvoice />,
      content: (
        <InvoiceCollectionInvoicesTab
          invoiceCollectionId={id}
          invoiceCollection={invoiceCollection}
        />
      ),
    },
    {
      title: i18n.t('InvoiceCollection:storesAndAccounts'),
      icon: <FaLink />,
      content: (
        <>
          <InvoiceCollectionStoresAccountsTab
            invoiceCollectionId={id}
            invoiceCollection={invoiceCollection}
            refetchInvoice={refetchInvoice}
            containerPrices={containerPrices}
            setContainerPrices={setContainerPrices}
          />
          <Button
            className="float-end mt-2"
            onClick={() => updateBillomatClient()}
            disabled={updatingBillomatClient}
          >
            {i18n.t('InvoiceCollection:updateBillomatClient')}
          </Button>
        </>
      ),
    },
    {
      title: i18n.t('Tabs:changeHistory'),
      icon: <FaHistory />,
      content: (
        <InvoiceCollectionChangeHistory
          invoiceCollectionId={id}
          title={i18n.t('Tabs:changeHistory')}
        />
      ),
    },
  ];

  return (
    <div className="mt-5">
      <h3 className="d-inline-block me-3">{invoiceCollection.name}</h3>
      <Badge
        color={
          invoiceCollection.scope === InvoiceCollectionScope.PenaltyBillsOnly
            ? 'warning'
            : 'secondary'
        }
        title={i18n.t('InvoiceCollection:scopeImmutableHint')}
      >
        {i18n.t('InvoiceCollection:scope')}:{' '}
        {invoiceCollection.scope === InvoiceCollectionScope.PenaltyBillsOnly
          ? i18n.t('InvoiceCollection:scopePenaltyBillsOnly')
          : i18n.t('InvoiceCollection:scopeAll')}
      </Badge>
      {invoiceCollection.scope === InvoiceCollectionScope.PenaltyBillsOnly && (
        <p className="text-muted mb-0">
          {i18n.t('InvoiceCollection:penaltyScopeHint')}
        </p>
      )}
      <TabsComponentBootstrap
        className="mt-3 mb-4"
        tabs={tabs}
        activeTab={activeTab}
      />
    </div>
  );
}
